import { ReactNode, createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

type AlertColor = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarMessage {
  message: string;
  severity: AlertColor;
  autoHideDuration?: number;
}

interface SnackbarContextType {
  showAlert: (status: AlertColor, message?: string) => void;
  hideSnackbar: () => void;
  currentMessage: SnackbarMessage | null;
  isOpen: boolean;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const { t } = useTranslation();
  const [currentMessage, setCurrentMessage] = useState<SnackbarMessage | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const showAlert = (status: AlertColor, message?: string) => {
    const finalMessage = message || (status === 'success' ? t('common:info-saved') : t('common:save-failed'));
    setCurrentMessage({ message: finalMessage, severity: status, autoHideDuration: 3000 });
    setIsOpen(true);
  };

  const hideSnackbar = () => {
    setIsOpen(false);
    setCurrentMessage(null);
  };

  const value: SnackbarContextType = {
    showAlert,
    hideSnackbar,
    currentMessage,
    isOpen,
  };

  return <SnackbarContext.Provider value={value}>{children}</SnackbarContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}
